import { useTranslation } from 'react-i18next';
import { Button, Tag } from '@digdir/designsystemet-react';
import { SettingsSection } from '../TeamSettingsLayout';
import { Alert } from '../Alert/Alert';
import { useSSOSettings } from '../../features/sso/hooks/useSSOSettings';
import { SSOConnectionUtils } from '../../features/sso/utils/ssoConnectionUtils';
import { ConnectionsSection } from './ConnectionsSection';
import { ReEnableForm } from './ReEnableForm';
import { FirstTimeSetupForm } from './FirstTimeSetupForm';
import { SetupInstructions } from './SetupInstructions';
import classes from './SSOSettings.module.css';

export const SSOSettings = () => {
  const { t } = useTranslation();
  const {
    organization,
    ssoStatus,
    isLoading,
    error,
    isSuccess,
    showEnableForm,
    formErrors,
    isEnabling,
    handleEnableSSO,
    handleDisableSSO,
    openEnableForm,
    closeEnableForm,
    isFieldRequired,
    uiStateManager,
  } = useSSOSettings();

  if (isLoading) {
    return (
      <div className={classes.loadingState}>
        <div className={classes.spinner} />
        <p>{t('ssoSettings.loading')}</p>
      </div>
    );
  }

  if (!organization) {
    return (
      <Alert variant="danger" size="md" title={t('common.errors.defaultError')}>
        {t('ssoSettings.errors.organizationNotFound')}
      </Alert>
    );
  }

  const connections = ssoStatus?.connections || [];
  const hasDisabledConnection = SSOConnectionUtils.hasDisabledConnection(connections);
  const showSetupWizard = !showEnableForm && connections.length === 0;

  return (
    <>
      {error && (
        <Alert variant="danger" size="md" title={t('common.errors.defaultError')} className={classes.alert}>
          {error instanceof Error ? error.message : t('common.errors.defaultError')}
        </Alert>
      )}
      {isSuccess && (
        <Alert variant="success" size="md" title={t('common.success')} className={classes.alert}>
          {isEnabling
            ? t('ssoSettings.success.enabled')
            : t('ssoSettings.success.disabled')}
        </Alert>
      )}

      <SettingsSection title={t('ssoSettings.title')} description={t('ssoSettings.subtitle')}>
        <div className={classes.body}>
          {showSetupWizard && (
            <div className={classes.statusRow}>
              <div className={classes.status}>
                <Tag data-size="sm" data-color="neutral" className={classes.statusTag}>
                  {t('ssoSettings.notEnabled')}
                </Tag>
                <span>Microsoft Entra ID</span>
              </div>
              <Button variant="secondary" onClick={openEnableForm} className={classes.statusButton}>
                {t('ssoSettings.enable')}
              </Button>
            </div>
          )}
          {connections.length > 0 && (
            <ConnectionsSection
              connections={connections}
              uiStateManager={uiStateManager}
              onDisable={(id) =>
                handleDisableSSO(id, t('ssoSettings.activeConnections.confirmDisable'))
              }
              onReEnable={openEnableForm}
              t={t}
            />
          )}
          {hasDisabledConnection && showEnableForm && (
            <ReEnableForm
              formErrors={formErrors}
              isEnabling={isEnabling}
              onSubmit={handleEnableSSO}
              onCancel={closeEnableForm}
              isFieldRequired={isFieldRequired}
              t={t}
            />
          )}
          {!hasDisabledConnection && showEnableForm && (
            <FirstTimeSetupForm
              formErrors={formErrors}
              isEnabling={isEnabling}
              onSubmit={handleEnableSSO}
              onCancel={closeEnableForm}
              isFieldRequired={isFieldRequired}
              t={t}
            />
          )}
          <SetupInstructions t={t} />
        </div>
      </SettingsSection>
    </>
  );
};
